import type { CoachingSession } from '@efdigital/ll-addon-data';
import type CoachService from '@efdigital/ll-xceed-v2/services/coach';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import BsModal from 'ember-bootstrap/components/bs-modal';

interface DeclineBookingModalSignature {
  Args: {
    session: CoachingSession;
    onChanged?: () => void | Promise<void>;
  };
  Blocks: {
    default: [showModal: () => void];
  };
}

export class DeclineBookingModal extends Component<DeclineBookingModalSignature> {
  @service declare coach: CoachService;
  @tracked open = false;
  @tracked loading = false;
  @tracked cancelReason = '';
  @tracked errorMessage = '';

  closeModal = () => {
    this.open = false;
  };

  get confirmButtonDisabled(): boolean {
    return this.cancelReason.trim().length === 0 || this.loading;
  }

  confirmDecline = async () => {
    const reason = this.cancelReason.trim();
    if (!reason) {
      return;
    }

    this.loading = true;
    this.errorMessage = '';
    try {
      await this.coach.rejectSession(this.args.session, reason);
      await this.args.onChanged?.();
      this.open = false;
    } catch {
      this.errorMessage = 'Could not decline this booking. Please try again.';
    } finally {
      this.loading = false;
    }
  };

  setCancelReason = (event: Event) => {
    const target = event.target as HTMLInputElement;
    this.cancelReason = target.value;
  };

  showModal = () => {
    this.cancelReason = '';
    this.errorMessage = '';
    this.open = true;
  };

  <template>
    {{#if this.open}}
      <BsModal @open={{this.open}} as |modal|>
        <modal.header>
          <h5 class="modal-title">Decline booking</h5>
        </modal.header>
        <modal.body aria-busy={{this.loading}}>
          <p>Are you sure you want to decline this session? The coachee will be
            notified and their session credit will be refunded.</p>

          <div class="mb-3">
            <label for="declineReason-{{@session.id}}" class="form-label">Reason
              for declining</label>
            <input
              type="text"
              class="form-control"
              id="declineReason-{{@session.id}}"
              required
              value={{this.cancelReason}}
              {{on "input" this.setCancelReason}}
            />
          </div>
          {{#if this.errorMessage}}
            <div class="alert alert-danger mb-0" role="alert">
              {{this.errorMessage}}
            </div>
          {{/if}}
        </modal.body>
        <modal.footer>
          <button
            class="btn btn-outline-primary"
            type="button"
            {{on "click" this.closeModal}}
            disabled={{this.loading}}
          >Decide later</button>
          <button
            class="btn btn-primary"
            type="button"
            {{on "click" this.confirmDecline}}
            disabled={{this.confirmButtonDisabled}}
            aria-busy={{this.loading}}
          >Decline booking</button>
        </modal.footer>
      </BsModal>
    {{/if}}
    {{yield this.showModal}}
  </template>
}
